import { Outlet, useLocation } from 'react-router-dom';
import PlatformShell from './PlatformShell';
import { Toaster } from '@/components/ui/toaster';
import { AgentApprovalManager } from '@/components/trading/AgentApprovalManager';
import { TradingAgentTabs } from '@/components/trading/TradingAgentTabs';
import { useAgentStore } from '@/stores/agentStore';
import { cn } from '@/lib/utils';

interface MainLayoutProps {
  className?: string;
}

/** T034: Platform shell + launcher (no sidebar) or app content. */
export default function MainLayout({ className }: MainLayoutProps) {
  const { pathname } = useLocation();
  const isTradingAgent = pathname.startsWith('/apps/trading-agent');
  const isLlmChat = pathname.startsWith('/apps/llm-chat');
  const isSmartNews = pathname.startsWith('/apps/smart-news');
  const isSurfTime = pathname.startsWith('/apps/surftime');
  const isLocalTea = pathname.startsWith('/apps/localtea');
  const isBitzBuggy = pathname.startsWith('/apps/bitzbuggy');
  const isLauncher = pathname === '/' || pathname === '/apps';
  const isPlatformAdmin = pathname === '/admin';
  const isPlatformPage = isLauncher || isPlatformAdmin;

  // Pending-approvals badge count for the Trading Agent Approvals tab.
  // Subscribed selectively so non-trading routes don't re-render on updates.
  const approvalsCount = useAgentStore((s) =>
    isTradingAgent ? s.pendingApprovals.length : 0,
  );

  // NOTE: per-app theme class (`.theme-{app}[data-mode="..."]`) is applied
  // to <body> by <ThemeBodyClass> in App.tsx so the tokens.css cascade wins
  // body-level over legacy scoped CSS. We do NOT re-add the class on
  // <main>/wrapping divs — that creates double-scoping and lets legacy
  // app-specific CSS (e.g. theme-trading-agent.css) over-specify tokens.

  return (
    <div
      className={cn(
        'flex h-screen flex-col',
        !isPlatformPage && !isLlmChat && !isSurfTime && !isSmartNews && !isLocalTea && !isBitzBuggy && !isTradingAgent && 'bg-background',
        className
      )}
    >
      <PlatformShell />
      <div className="flex flex-1 overflow-hidden">
        {isTradingAgent ? (
          /* Trading Agent uses ONLY top tabs (FR-025) — no left sidebar.
             The legacy Sidebar was redundant with the tab bar. */
          <main className="app-workspace flex-1 flex flex-col overflow-hidden">
            <TradingAgentTabs
              basePath="/apps/trading-agent"
              approvalsCount={approvalsCount}
            />
            <div className="flex-1 overflow-y-auto">
              {/* Wider than Tailwind's default `container` (1280px xl cap) —
                  the terminal dashboard mockup fills the viewport comfortably
                  up to ~1600px, so desktop users don't stare at side gutters. */}
              <div className="mx-auto max-w-[1600px] p-4 lg:px-8 lg:py-6">
                <Outlet />
              </div>
            </div>
          </main>
        ) : isLlmChat ? (
          <main className="flex-1 flex flex-col overflow-hidden relative">
            {/* Decorative backdrop — MUST be pointer-events:none or it
                blankets every LLM-chat route (chat, admin) and swallows
                every click. The legacy `.llm-chat-bg` CSS was removed in
                spec 015 so no rule was masking this any more. */}
            <div
              className="llm-chat-bg absolute inset-0 pointer-events-none"
              aria-hidden
            />
            <div className="llm-chat-inner flex-1 overflow-hidden">
              <Outlet />
            </div>
          </main>
        ) : (
          <main
            className={cn(
              'flex-1 overflow-y-auto',
              isPlatformPage && 'launcher-main'
            )}
          >
            {isPlatformPage || isSmartNews || isSurfTime || isLocalTea || isBitzBuggy ? (
              /* Wide-canvas apps render their own outer container with whatever
                 horizontal padding makes sense for the data. BitzBuggy joined
                 this list once the Vehicle Detail page outgrew a 1600px cap —
                 it's a multi-panel dashboard, not a prose surface. */
              <Outlet />
            ) : (
              <div className="mx-auto max-w-[1600px] p-4 lg:px-8 lg:py-6">
                <Outlet />
              </div>
            )}
          </main>
        )}
      </div>
      <Toaster />
      <AgentApprovalManager />
    </div>
  );
}
